import MasterModal from 'components/organisms/masterModal';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import { MonthlyInteractionCallForm } from 'components/organisms/device-troubleshoot-templates/templates/deviceTroubleshootForm';
import FormItemWrapper from 'components/molecules/rpm-alert-assessment/formItemWrapper';
import { useEffect, useState, type FC } from 'react';

import s from '../../styles.module.scss';
import { useStoreModalValue } from '../../hooks/use-store-modal-value';
import { useMonthlyInteractionCall } from '../../hooks/use-monthly-interaction-call';
import AlertDraggableModal from '../alert-draggable-modal';

import type { IMonthlyIntecationCallModalProps } from '../../types';

const MonthlyInteractionCall: FC<IMonthlyIntecationCallModalProps> = ({
  isOpen,
  handleSubmit,
  closeModal,
  whichPerson,
  otherPerson,
  patientGuid,
  toggleOpenWrapUpModal,
  toggleOpenMonthlyInteractionCall,
  onAppointmentChange,
}) => {
  const { t } = useTranslation();
  const { initialValues, onSaveForm, selectAppointmentPayload } =
    useMonthlyInteractionCall({
      closeModal,
      handleSubmit,
      patientGuid,
      whichPerson,
      otherPerson,
    });
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    }
  }, []);

  const type = 'min';

  type TInitialValues = typeof initialValues;
  const { parsedData, setModalValuesToStorage } =
    useStoreModalValue<TInitialValues>({
      modalName: `monthlyInteractionCall_${patientGuid}`,
    });

  const formik = useFormik({
    initialValues: parsedData ?? initialValues,
    onSubmit: (data) => {
      setModalValuesToStorage(data);
      onSaveForm(data);
    },
    enableReinitialize: true,
  });

  const onSave = () => {
    formik.submitForm();
  };
  useEffect(() => {
    const isPatientHaveExpEtcField = formik.getFieldMeta('isPatientHaveExpEtc');
    if (
      isPatientHaveExpEtcField &&
      isPatientHaveExpEtcField.value === 'yes' &&
      isMounted
    ) {
      setShowAlert(true);
    }
  }, [formik.values.isPatientHaveExpEtc, isMounted]);

  useEffect(() => {
    if (formik.values.recentUpcomingAppointments !== 'yes') {
      onAppointmentChange?.(null);
    }
    const details = selectAppointmentPayload(formik.values);
    if (!details) return;

    onAppointmentChange?.(details);
  }, [formik.values, selectAppointmentPayload, onAppointmentChange]);

  const expandableExtraFields = [
    'additionalEducationalMaterialsSent',
    'accessToReadingHealtcare',
    'recentUpcomingAppointments',
    'isPatientCompleteRpmSMSForm',
  ];
  const expandableExtraExtraFields = ['isPatientHaveExpEtc'];

  const closeAlert = () => setShowAlert(false);

  return (
    <>
      {isMounted && showAlert && (
        <AlertDraggableModal
          toggle={closeAlert}
          handleOk={async () => {
            await formik.submitForm();

            toggleOpenMonthlyInteractionCall(false);
            toggleOpenWrapUpModal(true);
          }}
          isOpen={showAlert}
        >
          <div className={s['alert-wrapper']}>
            <p>{t('rpmAlertAssessment.escalateToNursingTeam')}</p>
          </div>
        </AlertDraggableModal>
      )}
      <MasterModal
        isOpen={isOpen}
        toggle={closeModal}
        title={t('labels.RPMSpecialistTemplate')}
        onSave={onSave}
        size="medium"
      >
        <BaseForm formik={formik}>
          <div className={s.wrapper}>
            {MonthlyInteractionCallForm.map((formItem, index) => {
              const isShowExtraFields = expandableExtraFields.includes(
                formItem.name,
              )
                ? formik.getFieldMeta(formItem.name)?.value === 'yes'
                : undefined;
              const isShowExtraExtraFields = formItem.extra
                ? formItem.extra.some((extraItem) => {
                    return expandableExtraExtraFields.includes(extraItem.name)
                      ? formik.getFieldMeta(extraItem.name)?.value === 'yes'
                      : undefined;
                  })
                : undefined;

              return (
                <FormItemWrapper
                  formik={formik}
                  index={index}
                  formItem={formItem}
                  type={type}
                  key={formItem.key}
                  requiredSymptomFields={[]}
                  isShowExtraFields={isShowExtraFields}
                  isShowExtraExtraFields={isShowExtraExtraFields}
                  patientGuid={patientGuid}
                />
              );
            })}
          </div>
        </BaseForm>
      </MasterModal>
    </>
  );
};

export default MonthlyInteractionCall;
